import {
  FormControl,
  FormControlError,
  FormControlErrorText,
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from '@vidahealth/vida-ui';
import { ChevronDown } from 'lucide-react-native';
import { useState, type ReactNode } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { View } from 'react-native';

import { FloatingLabel } from '../FloatingLabel';

export type SelectOption = { value: string; label: string };

type ControlledSelectFieldProps = {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Floating label rendered above the trigger. */
  label?: ReactNode;
  isDisabled?: boolean;
  errorMessage?: string;
  testID?: string;
};

/**
 * Controlled dropdown over vida-ui's gluestack Select, for fixed option sets
 * (gender, ethnicity, the feet/inches pickers). Use this directly when the
 * value lives in plain state (e.g. the rules-engine draft); use the RHF-bound
 * `SelectField` below when inside a react-hook-form. `Autocomplete` is for
 * searching a large catalog; this is for short, known lists.
 */
export function ControlledSelectField({
  value,
  onValueChange,
  options,
  placeholder,
  label,
  isDisabled,
  errorMessage,
  testID,
}: ControlledSelectFieldProps) {
  const [fieldHeight, setFieldHeight] = useState(0);
  const selectedLabel = options.find((o) => o.value === value)?.label;
  const hasError = !!errorMessage;
  // A select has no text-focus state, so it floats its label once a value is
  // chosen and rests it in the placeholder position while empty.
  const active = value != null && value !== '';
  const fieldLabel = label ?? placeholder;
  const labelColorClass = hasError
    ? 'text-error-700'
    : active
      ? 'text-typography-700'
      : 'text-typography-500';

  return (
    <FormControl isInvalid={hasError} isDisabled={isDisabled}>
      <View className='relative' onLayout={(e) => setFieldHeight(e.nativeEvent.layout.height)}>
        <Select selectedValue={value} initialLabel={selectedLabel} onValueChange={onValueChange}>
          <SelectTrigger variant='outline' size='lg'>
            {/* Native placeholder omitted — the FloatingLabel is the placeholder. */}
            <SelectInput
              className='flex-1'
              accessibilityLabel={typeof fieldLabel === 'string' ? fieldLabel : undefined}
              testID={testID}
            />
            <SelectIcon as={ChevronDown} className='mr-3' />
          </SelectTrigger>
          <SelectPortal>
            <SelectBackdrop />
            <SelectContent>
              <SelectDragIndicatorWrapper>
                <SelectDragIndicator />
              </SelectDragIndicatorWrapper>
              {options.map((option) => (
                <SelectItem key={option.value} label={option.label} value={option.value} />
              ))}
            </SelectContent>
          </SelectPortal>
        </Select>
        {fieldLabel ? (
          <FloatingLabel
            label={fieldLabel}
            active={active}
            fieldHeight={fieldHeight}
            colorClass={labelColorClass}
          />
        ) : null}
      </View>
      {errorMessage ? (
        <FormControlError>
          <FormControlErrorText size='sm' className='ml-1 mt-1'>
            {errorMessage}
          </FormControlErrorText>
        </FormControlError>
      ) : null}
    </FormControl>
  );
}

type SelectFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  options: SelectOption[];
  placeholder?: string;
  label?: ReactNode;
  isDisabled?: boolean;
  testID?: string;
};

/**
 * RHF-integrated dropdown — a thin `Controller` wrapper around
 * `ControlledSelectField`. The counterpart to `TextField` / `CheckboxField`
 * for fixed option sets. Selecting an item writes its `value` into the form.
 */
export function SelectField<TFieldValues extends FieldValues>({
  control,
  name,
  options,
  placeholder,
  label,
  isDisabled,
  testID,
}: SelectFieldProps<TFieldValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <ControlledSelectField
          value={value}
          onValueChange={onChange}
          options={options}
          placeholder={placeholder}
          label={label}
          isDisabled={isDisabled}
          errorMessage={error?.message}
          testID={testID}
        />
      )}
    />
  );
}
